import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';

interface ReversePurchaseReceiptGLDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  receiptNumber: string;
  vendorName: string;
}

const ReversePurchaseReceiptGLDialog: React.FC<ReversePurchaseReceiptGLDialogProps> = ({
  open,
  onClose,
  onConfirm,
  receiptNumber,
  vendorName
}) => {
  const [isReversingTransaction, setIsReversingTransaction] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleConfirm = async () => {
    setIsReversingTransaction(true);
    setError(null);
    
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error('Error reversing GL transaction:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsReversingTransaction(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <UndoIcon sx={{ mr: 1 }} />
          Reverse GL Transaction
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to reverse the GL transaction for:
        </Typography>
        <Typography variant="subtitle1" fontWeight={600}>
          Purchase Receipt #{receiptNumber} - {vendorName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This will delete the GL transaction and remove its effects from the GL accounts.
          The purchase receipt will be marked as not having a GL transaction.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isReversingTransaction}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isReversingTransaction}
          startIcon={isReversingTransaction ? <CircularProgress size={20} color="inherit" /> : <UndoIcon />}
        >
          {isReversingTransaction ? 'Reversing...' : 'Reverse Transaction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReversePurchaseReceiptGLDialog;
